/**
 * Add / edit a card.
 *
 * Two things this deliberately does NOT do:
 *  - suggest issuers from a list. §2.1: the app ships knowing about zero banks.
 *    A dropdown of Indian issuers would be a hardcoded card list by another name.
 *  - ask for the full card number. §14: last 4 only, ever.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { CardObject } from "@/components/cards/CardObject";
import { Button, Field, Input } from "@/components/ui/primitives";
import { cardsApi, type Card, type CardInput } from "@/lib/api/client";

interface Props {
  card: Card | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CardFormDialog({ card, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const isEdit = Boolean(card);

  const [form, setForm] = useState({
    issuer: card?.issuer ?? "",
    product_name: card?.product_name ?? "",
    network: card?.network ?? "",
    last4: card?.last4 ?? "",
    nickname: card?.nickname ?? "",
    billing_day: card?.billing_day?.toString() ?? "",
    due_day: card?.due_day?.toString() ?? "",
    credit_limit: card?.credit_limit ?? "",
    pdf_password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const mutation = useMutation({
    mutationFn: (input: CardInput) =>
      isEdit ? cardsApi.update(card!.id, input) : cardsApi.create(input),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["info"] });
      toast.success(isEdit ? "Card updated." : `${saved.nickname || saved.issuer} added.`);
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.issuer.trim()) next.issuer = "Required — the bank that issued the card.";
    if (!/^\d{4}$/.test(form.last4)) next.last4 = "Exactly 4 digits.";
    for (const [k, label] of [
      ["billing_day", "Billing day"],
      ["due_day", "Due day"],
    ] as const) {
      const v = form[k];
      if (v && (!/^\d+$/.test(v) || +v < 1 || +v > 31)) {
        next[k] = `${label} must be between 1 and 31.`;
      }
    }
    if (form.credit_limit && !/^\d+(\.\d{1,2})?$/.test(form.credit_limit)) {
      next.credit_limit = "Numbers only, e.g. 250000 or 250000.50";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const input: CardInput = {
      issuer: form.issuer.trim(),
      product_name: form.product_name.trim() || null,
      network: form.network.trim().toLowerCase() || null,
      last4: form.last4,
      nickname: form.nickname.trim() || null,
      billing_day: form.billing_day ? +form.billing_day : null,
      due_day: form.due_day ? +form.due_day : null,
      credit_limit: form.credit_limit || null,
    };
    // Only send a password when one was typed — an empty field on edit must
    // not wipe a saved password (the API treats "" as an explicit clear).
    if (form.pdf_password) input.pdf_password = form.pdf_password;

    mutation.mutate(input);
  }

  // Live preview of the card object, so the user sees what lands on the wall.
  const preview: Card = {
    ...(card ?? {
      id: "preview",
      theme: null,
      billing_day_pinned: false,
      due_day_pinned: false,
      credit_limit_pinned: false,
      status: "active" as const,
      source: "manual" as const,
      has_pdf_password: false,
      created_at: "",
      updated_at: "",
      credit_limit: null,
      billing_day: null,
      due_day: null,
      rewards_note: null,
    }),
    issuer: form.issuer || "Issuer",
    product_name: form.product_name || null,
    network: form.network || null,
    last4: /^\d{4}$/.test(form.last4) ? form.last4 : "0000",
    nickname: form.nickname || null,
    // Existing cards keep their saved theme; new ones show the fallback until
    // the server derives one from the issuer name.
    theme: card?.theme ?? null,
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 max-h-[92vh] w-[min(46rem,94vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto dialog-content"
        >
          <div className="flex items-center justify-between border-b border-hairline px-5 py-4">
            <Dialog.Title className="font-display text-lg font-semibold text-ink">
              {isEdit ? "Edit card" : "Add a card"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <form onSubmit={submit} className="grid gap-6 p-5 md:grid-cols-[1fr_15rem]">
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Issuer" error={errors.issuer}>
                  <Input
                    value={form.issuer}
                    onChange={set("issuer")}
                    placeholder="The bank on the statement"
                    autoFocus
                  />
                </Field>
                <Field label="Last 4 digits" error={errors.last4}>
                  <Input
                    value={form.last4}
                    onChange={set("last4")}
                    placeholder="4321"
                    inputMode="numeric"
                    maxLength={4}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Product name" hint="As the bank calls it.">
                  <Input value={form.product_name} onChange={set("product_name")} />
                </Field>
                <Field label="Nickname" hint="What you call it. Shown everywhere.">
                  <Input value={form.nickname} onChange={set("nickname")} />
                </Field>
              </div>

              <Field label="Network" hint="visa · mastercard · rupay · amex · diners">
                <Input value={form.network} onChange={set("network")} />
              </Field>

              <div className="hairline-divider" />

              <p className="text-xs text-faint">
                Leave the rest blank if you don't know them — MyDeck learns billing day, due
                day, and limit from your statements.
              </p>

              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Billing day" error={errors.billing_day}>
                  <Input
                    value={form.billing_day}
                    onChange={set("billing_day")}
                    inputMode="numeric"
                    placeholder="—"
                  />
                </Field>
                <Field label="Due day" error={errors.due_day}>
                  <Input
                    value={form.due_day}
                    onChange={set("due_day")}
                    inputMode="numeric"
                    placeholder="—"
                  />
                </Field>
                <Field label="Credit limit" error={errors.credit_limit}>
                  <Input
                    value={form.credit_limit}
                    onChange={set("credit_limit")}
                    inputMode="decimal"
                    placeholder="—"
                  />
                </Field>
              </div>

              <Field
                label="Statement PDF password"
                hint={
                  card?.has_pdf_password
                    ? "A password is saved. Type to replace it; leave blank to keep it."
                    : "Encrypted before it's stored. MyDeck needs it to open the PDFs."
                }
              >
                <Input
                  type="password"
                  value={form.pdf_password}
                  onChange={set("pdf_password")}
                  placeholder={card?.has_pdf_password ? "••••••••" : ""}
                  autoComplete="new-password"
                />
              </Field>
            </div>

            {/* Preview */}
            <div className="space-y-3">
              <span className="label-micro block">Preview</span>
              <CardObject card={preview} showFacts />
              <p className="text-xs text-faint">
                Colours are derived from the issuer name. You'll be able to override them
                per card.
              </p>
            </div>

            <div className="flex justify-end gap-2 md:col-span-2">
              <Dialog.Close asChild>
                <Button variant="ghost" type="button">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button variant="primary" type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? "Saving…" : isEdit ? "Save changes" : "Add card"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
